import { DecimalPipe } from '@angular/common';
import { ColDef } from 'ag-grid-community';
import {
    AgGridLinkCellRendererComponent,
    AgGridLinkCellRendererParams,
} from '../../../../Compliance/AgGrid/CellRenderers/agGridLinkCellRenderer.component';
import { AgGridColumns, AgGridFilterParams } from '../../../../Compliance/AgGrid';
import { WeissmanDateFormatPipe } from '../../../../UI-Lib/Pipes';

const getReferralAppeal = (params: AgGridLinkCellRendererParams): Core.LitigationReferralAppealModel =>
    params.data as Core.LitigationReferralAppealModel;

const getSiteLink = (params: AgGridLinkCellRendererParams): string => {
    const referralAppeal = getReferralAppeal(params);
    return referralAppeal?.siteId ? `#/site/${referralAppeal.siteId}` : '';
};

const getParcelLink = (params: AgGridLinkCellRendererParams): string => {
    const referralAppeal = getReferralAppeal(params);
    return referralAppeal?.parcelId ? `#/parcel/${referralAppeal.parcelId}` : '';
};

const getAppealLink = (params: AgGridLinkCellRendererParams): string => {
    const referralAppeal = getReferralAppeal(params);
    return referralAppeal?.appealId ? `#/appeal/${referralAppeal.appealId}` : '';
};

export const REFERRAL_APPEAL_COLUMNS = (decimalPipe: DecimalPipe, datePipe: WeissmanDateFormatPipe): ColDef[] => [
    {
        colId: 'bigCheckbox',
        width: AgGridColumns.selectionColumnWidth,
        minWidth: AgGridColumns.selectionColumnWidth,
        maxWidth: AgGridColumns.selectionColumnWidth,
        resizable: false,
        headerCheckboxSelection: true,
        checkboxSelection: true,
        suppressColumnsToolPanel: true,
        pinned: 'left',
        lockPinned: true,
    },
    {
        headerName: 'Primary',
        field: 'isPrimary',
        width: AgGridColumns.textColumnSmallWidth,
        valueFormatter: (params) => {
            if (!params || !params.value) {
                return '';
            }
            return 'X';
        },
    },
    {
        headerName: 'State',
        field: 'siteStateAbbr',
        width: AgGridColumns.textColumnWidth,
        filter: 'agTextColumnFilter',
        filterParams: AgGridFilterParams.textFilterParams,
        floatingFilterComponentParams: AgGridFilterParams.textFloatingFilterParams,
    },
    {
        headerName: 'Site Name',
        field: 'siteName',
        width: AgGridColumns.textColumnWidth,
        cellRenderer: AgGridLinkCellRendererComponent,
        cellRendererParams: {
            getHelpContentId: () => 'app.view-site',
            newWindow: true,
            getLink: getSiteLink,
            isDisabled: (params: AgGridLinkCellRendererParams) => !getReferralAppeal(params)?.siteId,
        },
        filter: 'agTextColumnFilter',
        filterParams: AgGridFilterParams.textFilterParams,
        floatingFilterComponentParams: AgGridFilterParams.textFloatingFilterParams,
    },
    {
        headerName: 'Site Number',
        field: 'siteNumber',
        width: AgGridColumns.textColumnWidth,
        cellRenderer: AgGridLinkCellRendererComponent,
        cellRendererParams: {
            getHelpContentId: () => 'app.view-site',
            newWindow: true,
            getLink: getSiteLink,
            isDisabled: (params: AgGridLinkCellRendererParams) => !getReferralAppeal(params)?.siteId,
        },
        filter: 'agTextColumnFilter',
        filterParams: AgGridFilterParams.textFilterParams,
        floatingFilterComponentParams: AgGridFilterParams.textFloatingFilterParams,
    },
    {
        headerName: 'Assessor Name',
        field: 'siteAssessorName',
        width: AgGridColumns.textColumnWidth,
        filter: 'agTextColumnFilter',
        filterParams: AgGridFilterParams.textFilterParams,
        floatingFilterComponentParams: AgGridFilterParams.textFloatingFilterParams,
    },
    {
        headerName: 'Parcel Acct Number',
        field: 'parcelAcctNum',
        width: AgGridColumns.textColumnWidth,
        cellRenderer: AgGridLinkCellRendererComponent,
        cellRendererParams: {
            getHelpContentId: () => 'app.view-parcel',
            newWindow: true,
            getLink: getParcelLink,
            isDisabled: (params: AgGridLinkCellRendererParams) => !getReferralAppeal(params)?.parcelId,
        },
        filter: 'agTextColumnFilter',
        filterParams: AgGridFilterParams.textFilterParams,
        floatingFilterComponentParams: AgGridFilterParams.textFloatingFilterParams,
        sort: 'asc',
        sortIndex: 0,
    },
    {
        headerName: 'Tax Year',
        field: 'taxYear',
        width: AgGridColumns.textColumnWidth,
        filter: 'agTextColumnFilter',
        filterParams: AgGridFilterParams.textFilterParams,
        floatingFilterComponentParams: AgGridFilterParams.textFloatingFilterParams,
        sort: 'asc',
        sortIndex: 1,
    },
    {
        headerName: 'Appeal Level',
        field: 'nextAppealLevel',
        width: AgGridColumns.textColumnWidth,
        cellRenderer: AgGridLinkCellRendererComponent,
        cellRendererParams: {
            getHelpContentId: () => 'app.view-appeal',
            newWindow: true,
            getLink: getAppealLink,
            isDisabled: (params: AgGridLinkCellRendererParams) => !getReferralAppeal(params)?.appealId,
        },
        filter: 'agTextColumnFilter',
        filterParams: AgGridFilterParams.textFilterParams,
        floatingFilterComponentParams: AgGridFilterParams.textFloatingFilterParams,
    },
    {
        headerName: 'Revision Desc',
        field: 'revisionDesc',
        width: AgGridColumns.textColumnWidth,
        filter: 'agTextColumnFilter',
        filterParams: AgGridFilterParams.textFilterParams,
        floatingFilterComponentParams: AgGridFilterParams.textFloatingFilterParams,
    },
    {
        headerName: 'Market Value',
        field: 'appealMarketValue',
        width: AgGridColumns.numericColumnWidth,
        filter: 'agNumberColumnFilter',
        filterParams: AgGridFilterParams.numberFilterParams,
        floatingFilterComponentParams: AgGridFilterParams.numberFloatingFilterParams,
        valueFormatter: (params) => decimalPipe.transform(params.value, '1.0-0'),
    },
    {
        headerName: 'Target Value',
        field: 'appealTargetValue',
        width: AgGridColumns.textColumnWidth,
        filter: 'agTextColumnFilter',
        filterParams: AgGridFilterParams.textFilterParams,
        floatingFilterComponentParams: AgGridFilterParams.textFloatingFilterParams,
        valueFormatter: (params) => (params.value ? decimalPipe.transform(params.value, '1.0-0') : ''),
    },
    {
        headerName: 'File By Date',
        field: 'appealDeadlineDate',
        width: AgGridColumns.textColumnWidth,
        filter: 'agTextColumnFilter',
        filterParams: AgGridFilterParams.textFilterParams,
        floatingFilterComponentParams: AgGridFilterParams.textFloatingFilterParams,
        valueFormatter: (params) => {
            if (!params.value) {
                return '';
            }

            const d = new Date(params.value);
            return datePipe.transform(d, true);
        },
    },
];

export const REFERRAL_ADD_APPEAL_COLUMNS = (decimalPipe: DecimalPipe, datePipe: WeissmanDateFormatPipe): ColDef[] => [
    {
        colId: 'bigCheckbox',
        width: AgGridColumns.selectionColumnWidth,
        minWidth: AgGridColumns.selectionColumnWidth,
        maxWidth: AgGridColumns.selectionColumnWidth,
        resizable: false,
        headerCheckboxSelection: true,
        checkboxSelection: true,
        suppressColumnsToolPanel: true,
        pinned: 'left',
        lockPinned: true,
    },
    {
        headerName: 'State',
        field: 'stateAbbr',
        width: AgGridColumns.textColumnWidth,
        filter: 'agTextColumnFilter',
        filterParams: AgGridFilterParams.textFilterParams,
        floatingFilterComponentParams: AgGridFilterParams.textFloatingFilterParams,
    },
    {
        headerName: 'Site Name',
        field: 'siteName',
        width: AgGridColumns.textColumnWidth,
        filter: 'agTextColumnFilter',
        filterParams: AgGridFilterParams.textFilterParams,
        floatingFilterComponentParams: AgGridFilterParams.textFloatingFilterParams,
    },
    {
        headerName: 'Site Number',
        field: 'siteNumber',
        width: AgGridColumns.textColumnWidth,
        filter: 'agTextColumnFilter',
        filterParams: AgGridFilterParams.textFilterParams,
        floatingFilterComponentParams: AgGridFilterParams.textFloatingFilterParams,
    },
    {
        headerName: 'Assessor Name',
        field: 'assessorName',
        width: AgGridColumns.textColumnWidth,
        filter: 'agTextColumnFilter',
        filterParams: AgGridFilterParams.textFilterParams,
        floatingFilterComponentParams: AgGridFilterParams.textFloatingFilterParams,
    },
    {
        headerName: 'Parcel Acct Number',
        field: 'parcelAcctNum',
        width: AgGridColumns.textColumnWidth,
        filter: 'agTextColumnFilter',
        filterParams: AgGridFilterParams.textFilterParams,
        floatingFilterComponentParams: AgGridFilterParams.textFloatingFilterParams,
    },
    {
        headerName: 'Property Type',
        field: 'propertyType',
        width: AgGridColumns.textColumnWidth,
        filter: 'agTextColumnFilter',
        filterParams: AgGridFilterParams.textFilterParams,
        floatingFilterComponentParams: AgGridFilterParams.textFloatingFilterParams,
    },
    {
        headerName: 'Tax Year',
        field: 'taxYear',
        width: AgGridColumns.textColumnWidth,
        filter: 'agTextColumnFilter',
        filterParams: AgGridFilterParams.textFilterParams,
        floatingFilterComponentParams: AgGridFilterParams.textFloatingFilterParams,
    },
    {
        headerName: 'Appeal Level',
        field: 'appealLevel',
        width: AgGridColumns.textColumnWidth,
        filter: 'agTextColumnFilter',
        filterParams: AgGridFilterParams.textFilterParams,
        floatingFilterComponentParams: AgGridFilterParams.textFloatingFilterParams,
    },
    {
        headerName: 'Revision Desc',
        field: 'revisionDesc',
        width: AgGridColumns.textColumnWidth,
        filter: 'agTextColumnFilter',
        filterParams: AgGridFilterParams.textFilterParams,
        floatingFilterComponentParams: AgGridFilterParams.textFloatingFilterParams,
    },
    {
        headerName: 'Market Value',
        field: 'appealMarketValue',
        width: AgGridColumns.numericColumnWidth,
        filter: 'agNumberColumnFilter',
        filterParams: AgGridFilterParams.numberFilterParams,
        floatingFilterComponentParams: AgGridFilterParams.numberFloatingFilterParams,
        valueFormatter: (params) => decimalPipe.transform(params.value, '1.0-0'),
    },
    {
        headerName: 'Target Value',
        field: 'appealTargetValue',
        width: AgGridColumns.textColumnWidth,
        filter: 'agTextColumnFilter',
        filterParams: AgGridFilterParams.textFilterParams,
        floatingFilterComponentParams: AgGridFilterParams.textFloatingFilterParams,
        valueFormatter: (params) => (params.value ? decimalPipe.transform(params.value, '1.0-0') : ''),
    },
    {
        headerName: 'File By Date',
        field: 'appealDeadlineDate',
        width: AgGridColumns.textColumnWidth,
        filter: 'agTextColumnFilter',
        filterParams: AgGridFilterParams.textFilterParams,
        floatingFilterComponentParams: AgGridFilterParams.textFloatingFilterParams,
        valueFormatter: (params) => {
            if (!params.value) {
                return '';
            }

            const d = new Date(params.value);
            return datePipe.transform(d, true);
        },
    },
];
